import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

interface ChatMessage {
  _id?: string;
  conversationId: string;
  sender: 'user' | 'visitor' | 'agent';
  senderName: string;
  message: string;
  createdAt: string;
}

interface ChatWidgetProps {
  userEmail?: string;
  userName?: string;
  userPhone?: string;
  mode: 'user' | 'visitor';
  fabPosition?: 'bottom-right' | 'top-right';
  fabLabel?: string;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ userEmail, userName, userPhone, mode, fabPosition = 'bottom-right', fabLabel = 'Ask Agent!' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [needsPhone, setNeedsPhone] = useState(false);
  const [needsVisitorInfo, setNeedsVisitorInfo] = useState(mode === 'visitor');
  const [visitorInfoSubmitted, setVisitorInfoSubmitted] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [fabExpanded, setFabExpanded] = useState(true);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpenRef = useRef(isOpen);

  // Smooth collapse/expand cycle for FAB: expanded 4s → collapsed 3s → repeat
  useEffect(() => {
    if (isOpen) return;
    setFabExpanded(true);
    let elapsed = 0;
    const EXPAND_MS = 4000;
    const COLLAPSE_MS = 3000;
    const CYCLE = EXPAND_MS + COLLAPSE_MS;
    const timer = setInterval(() => {
      elapsed += 500;
      const phase = elapsed % CYCLE;
      setFabExpanded(phase < EXPAND_MS);
    }, 500);
    return () => clearInterval(timer);
  }, [isOpen]);

  // Keep ref in sync for use inside socket callbacks
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && typeof Notification.requestPermission === 'function' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Helper to send browser notification
  const sendBrowserNotification = useCallback((title: string, body: string) => {
    if ('Notification' in window && typeof Notification === 'function' && Notification.permission === 'granted' && (document.hidden || !isOpenRef.current)) {
      try {
        const n = new Notification(title, {
          body,
          icon: '/assets/logo.png',
          tag: 'chat-notification',
        });
        n.onclick = () => { window.focus(); setIsOpen(true); setHasNewMessage(false); n.close(); };
        setTimeout(() => n.close(), 6000);
      } catch (e) { /* SW fallback not needed for desktop */ }
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Mark agent messages as read when chat is open
  const markMessagesAsRead = useCallback(async (convId: string) => {
    try {
      await fetch(`${API_BASE}/api/chat/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conversationId: convId })
      });
    } catch (err) {
      // Silent fail — non-critical
    }
  }, []);

  // When chat opens or new messages arrive, mark agent messages as read
  useEffect(() => {
    if (!isOpen || !conversationId) return;
    const hasUnreadAgent = messages.some(m => m.sender === 'agent');
    if (hasUnreadAgent) {
      markMessagesAsRead(conversationId);
    }
  }, [isOpen, conversationId, messages, markMessagesAsRead]);

  // Connect socket
  useEffect(() => {
    if (!conversationId) return;

    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('joinConversation', conversationId);
    });

    socket.on('newMessage', (msg: ChatMessage) => {
      setMessages(prev => {
        // Deduplicate by _id
        if (msg._id && prev.some(m => m._id === msg._id)) return prev;
        // Remove optimistic message (no _id) that matches this real message
        const filtered = prev.filter(m => !(m._id === undefined && m.message === msg.message && m.sender === msg.sender));
        return [...filtered, msg];
      });
      if (msg.sender === 'agent') {
        setIsTyping(false);
        if (!isOpenRef.current) {
          setHasNewMessage(true);
        }
        sendBrowserNotification(
          'New message from Agent',
          msg.message.length > 80 ? msg.message.slice(0, 80) + '…' : msg.message
        );
      }
    });

    socket.on('userTyping', ({ sender }: { sender: string }) => {
      if (sender === 'agent') setIsTyping(true);
    });

    socket.on('userStopTyping', ({ sender }: { sender: string }) => {
      if (sender === 'agent') setIsTyping(false);
    });

    socket.on('conversationClosed', () => {
      setMessages(prev => [...prev, {
        conversationId: conversationId!,
        sender: 'agent',
        senderName: 'System',
        message: 'This conversation has been closed by the agent. You can start a new one by sending a message.',
        createdAt: new Date().toISOString()
      }]);
    });

    socket.on('conversationDeleted', () => {
      setMessages([]);
      setConversationId(null);
      if (mode === 'visitor') {
        setNeedsVisitorInfo(true);
        setVisitorInfoSubmitted(false);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [conversationId, isOpen]);

  const startConversation = useCallback(async (phone?: string, vEmail?: string, vPhone?: string) => {
    setIsConnecting(true);
    try {
      const body: any = {};
      if (mode === 'user') {
        body.userEmail = userEmail;
        body.userName = userName || '';
        body.userPhone = phone || userPhone || '';
      } else {
        const visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        body.visitorId = visitorId;
        body.visitorEmail = vEmail || '';
        body.visitorPhone = vPhone || '';
      }

      const res = await fetch(`${API_BASE}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setConversationId(data.conversation._id);
        setMessages(data.messages || []);

        // For logged-in users: if no phone on profile, ask for it
        if (mode === 'user' && !userPhone?.trim() && !phone) {
          setNeedsPhone(true);
        }
      }
    } catch (err) {
      console.error('[ChatWidget] Failed to start conversation:', err);
    } finally {
      setIsConnecting(false);
    }
  }, [mode, userEmail, userName, userPhone]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setHasNewMessage(false);
    if (!conversationId && mode === 'user') {
      startConversation();
    }
  }, [conversationId, mode, startConversation]);

  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || !conversationId) return;

    const msgText = inputText.trim();
    setInputText('');

    // Optimistic add
    const optimisticMsg: ChatMessage = {
      conversationId,
      sender: mode === 'user' ? 'user' : 'visitor',
      senderName: userName || emailInput || 'You',
      message: msgText,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await fetch(`${API_BASE}/api/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversationId,
          sender: mode === 'user' ? 'user' : 'visitor',
          senderName: userName || emailInput || '',
          message: msgText
        })
      });
    } catch (err) {
      console.error('[ChatWidget] Failed to send message:', err);
    }
  }, [inputText, conversationId, mode, userName, emailInput]);

  const handlePhoneSubmit = useCallback(async () => {
    if (!/^\d{10}$/.test(phoneInput)) return;
    setNeedsPhone(false);
    // Update conversation with phone
    if (conversationId) {
      try {
        await fetch(`${API_BASE}/api/chat/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            conversationId,
            sender: 'user',
            senderName: userName || '',
            message: `My phone number is: ${phoneInput}`
          })
        });
      } catch (err) {
        console.error('[ChatWidget] Failed to send phone:', err);
      }
    }
    // Add system welcome after phone
    setMessages(prev => [...prev, {
      conversationId: conversationId || '',
      sender: 'agent',
      senderName: 'Bot',
      message: 'Thank you! An agent will be with you shortly. How can we help you today?',
      createdAt: new Date().toISOString()
    }]);
  }, [phoneInput, conversationId, userName]);

  const handleVisitorInfoSubmit = useCallback(async () => {
    if (!/^\d{10}$/.test(phoneInput) || !emailInput.includes('@')) return;
    setNeedsVisitorInfo(false);
    setVisitorInfoSubmitted(true);
    await startConversation(undefined, emailInput, phoneInput);
  }, [phoneInput, emailInput, startConversation]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (needsPhone) handlePhoneSubmit();
      else if (needsVisitorInfo) handleVisitorInfoSubmit();
      else handleSendMessage();
    }
  }, [needsPhone, needsVisitorInfo, handlePhoneSubmit, handleVisitorInfoSubmit, handleSendMessage]);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Styles — the actual button lives inside a wrapper that carries the golden flair
  const fabWrapperStyle: React.CSSProperties = {
    position: 'fixed',
    ...(fabPosition === 'top-right' ? { top: '20px' } : { bottom: '28px' }),
    right: '24px',
    borderRadius: '28px',
    padding: '3px',
    zIndex: 9999,
    overflow: 'hidden',
    animation: 'fabBounce 2.5s ease-in-out infinite',
    cursor: 'pointer',
  };

  const fabStyle: React.CSSProperties = {
    position: 'relative',
    height: '50px',
    borderRadius: '25px',
    background: 'rgba(59, 93, 231, 0.55)',
    backdropFilter: 'blur(8px)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: fabExpanded ? '8px' : '0px',
    padding: fabExpanded ? '0 20px 0 16px' : '0 14px',
    transition: 'padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), gap 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s, background 0.2s',
    zIndex: 2,
    overflow: 'hidden',
  };

  const chatBoxStyle: React.CSSProperties = {
    position: 'fixed',
    ...(fabPosition === 'top-right' ? { top: '80px' } : { bottom: '96px' }),
    right: '24px',
    width: '380px',
    maxWidth: 'calc(100vw - 48px)',
    height: '520px',
    maxHeight: 'calc(100vh - 120px)',
    borderRadius: '16px',
    background: '#fff',
    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9999,
    animation: 'chatSlideUp 0.3s ease-out',
  };

  const headerStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #4c6ef5 0%, #3b5de7 100%)',
    color: '#fff',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  };

  const messagesContainerStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: '#f8f9fb',
  };

  const inputContainerStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderTop: '1px solid #e9ecef',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    background: '#fff',
    flexShrink: 0,
  };

  return (
    <>
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .chat-fab-wrapper:hover {
          animation: none !important;
          transform: scale(1.05);
        }
        .chat-fab-wrapper:hover .chat-fab-inner {
          background: rgba(59, 93, 231, 0.75) !important;
        }
        @keyframes fabBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes goldenSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .chat-messages::-webkit-scrollbar { width: 5px; }
        .chat-messages::-webkit-scrollbar-track { background: transparent; }
        .chat-messages::-webkit-scrollbar-thumb { background: #c4c9d4; border-radius: 10px; }
        .chat-send-btn:hover { background: #3b5de7 !important; }
      `}</style>

      {/* Floating Action Button */}
      {!isOpen && (
        <div className="chat-fab-wrapper" style={fabWrapperStyle} onClick={handleOpen} title="Chat with Agent">
          {/* Golden flair spinning border */}
          <div style={{
            position: 'absolute', top: '-50%', left: '-50%',
            width: '200%', height: '200%',
            background: 'conic-gradient(from 0deg, transparent 0deg 60deg, #fbbf24 80deg, #f59e0b 90deg, #d97706 100deg, #fbbf24 110deg, #fef3c7 120deg, transparent 140deg 360deg)',
            animation: 'goldenSpin 3s linear infinite',
            zIndex: 0
          }} />
          {hasNewMessage && (
            <span style={{
              position: 'absolute', top: '-2px', right: '-2px',
              width: '18px', height: '18px', borderRadius: '50%',
              background: '#ef4444', border: '2px solid #fff',
              animation: 'pulse 1.5s infinite', zIndex: 5
            }} />
          )}
          <button className="chat-fab-inner" style={fabStyle}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span style={{
              color: '#fff', fontWeight: 700, fontSize: '0.92rem', letterSpacing: '0.3px',
              whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              display: 'inline-block', overflow: 'hidden',
              maxWidth: fabExpanded ? '120px' : '0px',
              opacity: fabExpanded ? 1 : 0,
              transition: 'max-width 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
            }}>{ fabLabel }</span>
          </button>
        </div>
      )}

      {/* Chat Box */}
      {isOpen && (
        <div style={chatBoxStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Chat with Agent</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.8 }}>We typically reply within minutes</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.1rem', transition: 'background 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
            >
              ✕
            </button>
          </div>

          {/* Messages Area */}
          <div className="chat-messages" style={messagesContainerStyle}>
            {/* Welcome message */}
            {messages.length === 0 && !needsPhone && !needsVisitorInfo && (
              <div style={{
                background: '#e8ecff', borderRadius: '12px', padding: '14px 16px',
                fontSize: '0.88rem', color: '#3b5de7', lineHeight: 1.5,
                maxWidth: '85%'
              }}>
                👋 Welcome! How can we assist you today? Type your message below and an agent will respond shortly.
              </div>
            )}

            {/* Phone number prompt for logged-in users */}
            {needsPhone && (
              <div style={{
                background: '#e8ecff', borderRadius: '12px', padding: '14px 16px',
                fontSize: '0.88rem', color: '#3b5de7', lineHeight: 1.5,
                maxWidth: '90%'
              }}>
                👋 Hello{userName ? `, ${userName}` : ''}! Before we connect you with an agent, could you please share your 10-digit phone number?
                <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                  <input
                    type="tel"
                    placeholder="Enter 10-digit number"
                    value={phoneInput}
                    onChange={e => setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onKeyDown={handleKeyDown}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: '8px',
                      border: '1px solid #c4c9d4', fontSize: '0.88rem', outline: 'none'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={handlePhoneSubmit}
                    disabled={phoneInput.length !== 10}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: 'none',
                      background: phoneInput.length === 10 ? '#4c6ef5' : '#c4c9d4',
                      color: '#fff', fontWeight: 600, cursor: phoneInput.length === 10 ? 'pointer' : 'not-allowed',
                      fontSize: '0.82rem'
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}

            {/* Visitor info prompt (homepage) */}
            {needsVisitorInfo && !visitorInfoSubmitted && (
              <div style={{
                background: '#e8ecff', borderRadius: '12px', padding: '14px 16px',
                fontSize: '0.88rem', color: '#3b5de7', lineHeight: 1.5,
                maxWidth: '90%'
              }}>
                👋 Welcome! Please share your details so we can assist you:
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="tel"
                    placeholder="10-digit phone number"
                    value={phoneInput}
                    onChange={e => setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    style={{
                      padding: '8px 12px', borderRadius: '8px',
                      border: '1px solid #c4c9d4', fontSize: '0.88rem', outline: 'none'
                    }}
                    autoFocus
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{
                      padding: '8px 12px', borderRadius: '8px',
                      border: '1px solid #c4c9d4', fontSize: '0.88rem', outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleVisitorInfoSubmit}
                    disabled={phoneInput.length !== 10 || !emailInput.includes('@')}
                    style={{
                      padding: '10px 16px', borderRadius: '8px', border: 'none',
                      background: (phoneInput.length === 10 && emailInput.includes('@')) ? '#4c6ef5' : '#c4c9d4',
                      color: '#fff', fontWeight: 600,
                      cursor: (phoneInput.length === 10 && emailInput.includes('@')) ? 'pointer' : 'not-allowed',
                      fontSize: '0.88rem'
                    }}
                  >
                    Start Chat
                  </button>
                </div>
              </div>
            )}

            {/* Message bubbles */}
            {messages.map((msg, i) => {
              const isAgent = msg.sender === 'agent';
              const isSystem = msg.senderName === 'System' || msg.senderName === 'Bot';
              return (
                <div
                  key={msg._id || `msg-${i}`}
                  style={{
                    display: 'flex',
                    justifyContent: isAgent ? 'flex-start' : 'flex-end',
                    marginBottom: '2px'
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: isAgent ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
                    background: isAgent
                      ? (isSystem ? '#f0f4ff' : '#e8ecff')
                      : 'linear-gradient(135deg, #4c6ef5 0%, #3b5de7 100%)',
                    color: isAgent ? '#2d3748' : '#fff',
                    fontSize: '0.88rem',
                    lineHeight: 1.5,
                    wordBreak: 'break-word'
                  }}>
                    {isAgent && !isSystem && (
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#4c6ef5', marginBottom: '2px' }}>
                        {msg.senderName || 'Agent'}
                      </div>
                    )}
                    {msg.message}
                    <div style={{
                      fontSize: '0.65rem',
                      opacity: 0.6,
                      marginTop: '4px',
                      textAlign: 'right'
                    }}>
                      {formatTime(msg.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {isTyping && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0' }}>
                <div style={{
                  display: 'flex', gap: '3px', background: '#e8ecff',
                  borderRadius: '12px', padding: '8px 14px'
                }}>
                  {[0, 1, 2].map(i => (
                    <span key={i} style={{
                      width: '6px', height: '6px', borderRadius: '50%', background: '#4c6ef5',
                      animation: `typingBounce 1.4s infinite ${i * 0.2}s`, display: 'inline-block'
                    }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {!needsPhone && !needsVisitorInfo && (
            <div style={inputContainerStyle}>
              <input
                ref={inputRef}
                type="text"
                placeholder={conversationId ? 'Type a message...' : 'Connecting...'}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!conversationId || isConnecting}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '0.9rem',
                  outline: 'none', transition: 'border-color 0.2s',
                  background: !conversationId ? '#f7f8fa' : '#fff'
                }}
                onFocus={e => (e.target.style.borderColor = '#4c6ef5')}
                onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
              />
              <button
                className="chat-send-btn"
                onClick={handleSendMessage}
                disabled={!inputText.trim() || !conversationId}
                style={{
                  width: '40px', height: '40px', borderRadius: '50%', border: 'none',
                  background: inputText.trim() ? '#4c6ef5' : '#e2e8f0',
                  color: '#fff', cursor: inputText.trim() ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s', flexShrink: 0
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes typingBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-5px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
